<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">Drone Inventory</h1>
        <p class="text-muted">Manage ADN's fleet of drones</p>
      </div>
      <div class="col-md-6 text-md-end">
        <button type="button" class="btn btn-primary" (click)="createDrone()">
          <i class="bi bi-plus-circle me-2"></i>
          Add Drone
        </button>
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Search -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by name, serial number, N-Number..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Status Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Status</label>
            <select class="form-select" [(ngModel)]="filterStatus" (change)="onFilterChange()">
              <option value="all">All Statuses</option>
              <option value="available">Available</option>
              <option value="assigned">Assigned</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          <!-- Manufacturer Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Manufacturer</label>
            <select class="form-select" [(ngModel)]="filterManufacturer" (change)="onFilterChange()">
              <option value="all">All Manufacturers</option>
              <option *ngFor="let mfr of manufacturers" [value]="mfr.id">{{ mfr.name }}</option>
            </select>
          </div>

          <!-- Sort -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Sort By</label>
            <select class="form-select" [(ngModel)]="sortBy" (change)="onSortChange()">
              <option value="createdAt">Date Added</option>
              <option value="name">Name</option>
              <option value="serialNumber">Serial Number</option>
            </select>
          </div>

          <!-- Sort Order -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Order</label>
            <select class="form-select" [(ngModel)]="sortOrder" (change)="onSortChange()">
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          @if (showClearFilters()) {
            <div class="row mt-3">
              <div class="col-12">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="resetFilters()">
                  <i class="bi bi-x-circle me-1"></i>
                  Clear Filters
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Drones Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
          <thead>
            <tr>
              <th>Drone</th>
              <th>Model</th>
              <th>Serial Number</th>
              <th>N-Number</th>
              <th>Status</th>
              <th class="text-center">Zoho</th>
              <th>Added</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="drones.length === 0">
              <td colspan="8" class="text-center text-muted py-4">
                No drones found
              </td>
            </tr>
            <tr *ngFor="let drone of drones" class="cursor-pointer" (click)="viewDrone(drone.id)">
              <td>
                <div class="d-flex align-items-center gap-3">
                  <div class="entity-avatar">
                    <i class="adn-icon adn-icon-drone-solid"></i>
                  </div>
                  <div>
                    <div class="fw-medium">{{ drone.name }}</div>
                    <small class="text-muted">{{ drone.manufacturerName || 'Unknown' }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span>{{ drone.modelName || 'Unknown' }}</span>
              </td>
              <td>
                <span class="font-monospace small">{{ drone.serialNumber }}</span>
              </td>
              <td>
                <span *ngIf="drone.nNumber" class="font-monospace small">{{ drone.nNumber }}</span>
                <span *ngIf="!drone.nNumber" class="text-warning"><i class="bi bi-exclamation-triangle"></i></span>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusClass(drone.status)">
                  <i class="bi"
                    [class.bi-check-circle]="drone.status === 'available'"
                    [class.bi-person-check]="drone.status === 'assigned'"
                    [class.bi-wrench]="drone.status === 'maintenance'"
                    [class.bi-archive]="drone.status === 'retired'"
                  ></i>
                  {{ getStatusLabel(drone.status) }}
                </span>
              </td>
              <td class="text-center">
                <i class="bi"
                   [ngClass]="[getZohoSyncStatusIcon(drone.zohoFsmSyncStatus), getZohoSyncStatusClass(drone.zohoFsmSyncStatus)]"
                   [title]="getZohoSyncStatusTooltip(drone)"
                ></i>
              </td>
              <td>
                <small class="text-muted">{{ formatDate(drone.createdAt) }}</small>
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-1"
                  (click)="viewDroneDetails(drone.id); $event.stopPropagation()"
                  title="View Details"
                >
                  <i class="bi bi-eye"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  (click)="editDrone(drone.id); $event.stopPropagation()"
                  title="Edit"
                >
                  <i class="bi bi-pencil"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer" *ngIf="totalPages > 1">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalDrones) }} of {{ totalDrones }} drones
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>
